import React, { useState, useEffect, useMemo, useRef } from "react";
import { useLocation } from "react-router-dom";
import {
  getExpenses,
  getPostData,
  getProjectsData,
  seacrhPosts,
} from "../../services/service.js";
import "../../assets/css/doctor.scss";
import "../../assets/css/pharmacy.scss";

import ListHeader from "../../molecules/ListHeader/ListHeader.js";
import DG from "../../atoms/DataTable/DataGrid.js";
import CustomPagination from "../../atoms/CustomPagination/index.jsx";
import { exportToExcel, exportToPDF, mapExpensesForExport } from "../../utils/exportUtils";
import { FileSpreadsheet, FileText } from "lucide-react";
import { toPng } from "html-to-image";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import { IMAGE_BASE_URL } from "../../config/api";
import ReactDOM from "react-dom/client";
import ExportCard from "../../components/common/ExportCard.jsx";
import CreateExpenseSideBar from "../../components/common/CreateExpenseSideBar.jsx";
import EditExpenseSideBar from "../../components/common/EditExpenseSideBar.jsx";
import { deleteExpense } from "../../services/service";
import ConfirmationModal from "../../atoms/ConfirmationModal";
import { toast } from "react-toastify";

const Expenses = () => {
  const [loader, setLoader] = useState(false);
  const [totalExpense, setTotalExpense] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [ExpenseData, setExpenseData] = useState("");
  const [projectId, setProjectId] = useState("");
  const [workerId, setWorkerId] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [vendorId, setVendorId] = useState("");
  const [userType, setUserType] = useState("");
  const [userId, setUserId] = useState("");
  const [createdByUser, setCreatedByUser] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const location = useLocation();
  const itemsPerPage = 10;

  // Column selection + export-as-images
  const [selectedColumns, setSelectedColumns] = useState([]);
  const [exportingImages, setExportingImages] = useState(false);
  const hiddenRenderRef = useRef(null);
  const [selectedRowIds, setSelectedRowIds] = useState([]);
  const [showCreateDrawer, setShowCreateDrawer] = useState(false);
  const [showEditDrawer, setShowEditDrawer] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [pendingDeleteId, setPendingDeleteId] = useState(null);

  const selectableColumns = useMemo(
    () => [
      { key: "Description", label: "Description" },
      { key: "Project", label: "Project" },
      { key: "Amount", label: "Amount" },
      { key: "Tax", label: "Tax" },
      { key: "Total Amount", label: "Total Amount" },
      { key: "Period", label: "Period" },
      { key: "Vendor", label: "Vendor" },
      { key: "Created By", label: "Created By" },
    ],
    []
  );

  const allColumnKeys = useMemo(() => selectableColumns.map((c) => c.key), [selectableColumns]);

  useEffect(() => {
    if (location.state?.projectId) {
      setProjectId(location.state.projectId);
    }
    if (location.state?.workerId) {
      setWorkerId(location.state.workerId);
    }
  }, [location]);

  const handlegetData = async (limit, page) => {
    setLoader(true);
    try {
      const response = await getExpenses(page, limit, projectId, workerId, searchQuery, '', {
        startDate,
        endDate,
        vendor_id: vendorId,
        created_by_role: userType,
        user_type: userType,
        user_id: userId,
        created_by_user: createdByUser
      });
      if (response?.success) {
        setExpenseData(response?.data);
        setTotalExpense(response?.pagination?.totalItems);
      }
    } catch (error) {
      console.error("Error fetching expenses data:", error);
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => {
    setLoader(true);
    const debounceTimer = setTimeout(() => {
      handlegetData(itemsPerPage, currentPage);
    }, 500);

    return () => clearTimeout(debounceTimer);
  }, [itemsPerPage, currentPage, projectId, workerId, searchQuery, startDate, endDate, vendorId, userType, userId, createdByUser]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleSearchChange = (filters) => {
    setProjectId(filters?.project_id || "");
    setWorkerId(filters?.worker_id || "");
    setStartDate(filters?.startDate || "");
    setEndDate(filters?.endDate || "");
    setVendorId(filters?.vendor_id || "");
    setUserType(filters?.created_by_role || filters?.user_type || "");
    setUserId(filters?.user_id || "");
    setCreatedByUser(filters?.created_by_user || filters?.user_id || "");
    setCurrentPage(1);
  };

  const handleExpenseSearchChange = (value) => {
    setSearchQuery(value);
    setCurrentPage(1);
  };

  const getFlattenedExpenses = () => mapExpensesForExport(Array.isArray(ExpenseData) ? ExpenseData : []);

  const handleExportExcel = () => {
    const rows = getFlattenedExpenses();
    exportToExcel(rows, "Expenses", "Expenses");
  };

  const handleExportPDF = () => {
    const rows = getFlattenedExpenses();
    const headers = ["Description", "Project", "Amount", "Tax", "Total Amount", "Period", "Vendor", "Created By"];
    exportToPDF(rows, headers, "landscape", "Expenses");
  };

  const handleClearSelection = () => setSelectedColumns([]);
  const dataUrlToBlob = async (dataUrl) => {
    const res = await fetch(dataUrl);
    return res.blob();
  };
  const fetchBlob = async (url) => {
    const res = await fetch(url, { cache: "no-store" });
    if (!res.ok) throw new Error(`Failed to fetch ${url}`);
    return res.blob();
  };
  const getFileNameFromUrl = (url, fallback) => {
    try {
      const u = new URL(url);
      const last = u.pathname.split("/").filter(Boolean).pop();
      return last || fallback;
    } catch {
      return fallback;
    }
  };
  const mimeToExt = (mime) => {
    if (!mime) return null;
    const map = {
      'image/jpeg': 'jpg',
      'image/jpg': 'jpg',
      'image/png': 'png',
      'image/gif': 'gif',
      'image/webp': 'webp',
      'image/bmp': 'bmp',
      'image/tiff': 'tiff',
      'application/pdf': 'pdf',
    };
    return map[mime.toLowerCase()] || null;
  };
  const ensureExtension = (name, extFromMime, url) => {
    const hasDotExt = /\.[A-Za-z0-9]{2,6}$/.test(name);
    if (hasDotExt) return name;
    let ext = extFromMime;
    if (!ext && url) {
      const m = url.match(/\.([A-Za-z0-9]{2,6})(?:\?|#|$)/);
      if (m && m[1]) ext = m[1];
    }
    if (!ext) ext = 'jpg';
    return `${name}.${ext}`;
  };
  const normalizeImageUrl = (pathOrUrl) => {
    if (!pathOrUrl) return null;
    if (/^https?:\/\//i.test(pathOrUrl)) return pathOrUrl;
    const cleaned = pathOrUrl.startsWith("/") ? pathOrUrl.slice(1) : pathOrUrl;
    const base = IMAGE_BASE_URL.endsWith("/") ? IMAGE_BASE_URL : IMAGE_BASE_URL + "/";
    return base + cleaned;
  };
  const exportSelectedColumnsAsImages = async () => {
    if (!Array.isArray(ExpenseData) || ExpenseData.length === 0) return;
    try {
      setExportingImages(true);

      const container = hiddenRenderRef.current;
      if (!container) return;

      // Clear any previous children
      container.innerHTML = "";

      // Build cards offscreen using reusable component
      const selectedSet = new Set(selectedRowIds);
      const itemsToExport = ExpenseData.filter((it) => selectedSet.size === 0 || selectedSet.has(it?.id));
      itemsToExport.forEach((item, idx) => {
        const mountNode = document.createElement("div");
        container.appendChild(mountNode);
        const root = ReactDOM.createRoot(mountNode);
        root.render(
          React.createElement(ExportCard, {
            item,
            index: idx,
            selectedColumns,
            allColumnKeys,
            currencyCode: undefined,
          })
        );
      });

      // Wait for DOM to paint
      await new Promise((r) => setTimeout(r, 50));

      const zip = new JSZip();
      const folder = zip.folder("expenses")

      const children = Array.from(hiddenRenderRef.current.children);
      let index = 1;
      const selectedSetForImages = new Set(selectedRowIds);
      const itemsForImages = ExpenseData.filter((it) => selectedSetForImages.size === 0 || selectedSetForImages.has(it?.id));

      for (let i = 0; i < children.length; i++) {
        const node = children[i];
        // Ensure node is an HTMLElement
        if (!(node instanceof HTMLElement)) continue;
        const dataUrl = await toPng(node, { cacheBust: true, pixelRatio: 2 });
        const blob = await dataUrlToBlob(dataUrl);
        folder.file(`expense-${index}-card.png`, blob);

        // Also include original invoice image(s)
        const item = itemsForImages[i];
        const images = Array.isArray(item?.invoice_image)
          ? item.invoice_image
          : (item?.invoice_image ? [item.invoice_image] : []);
        let imageIdx = 1;
        for (const raw of images) {
          const url = normalizeImageUrl(raw);
          if (!url) continue;
          try {
            const invoiceBlob = await fetchBlob(url);
            const guessedExt = mimeToExt(invoiceBlob.type);
            const baseName = getFileNameFromUrl(url, `expense-${index}-invoice-${imageIdx}`);
            const safeName = ensureExtension(baseName, guessedExt, url);
            folder.file(safeName, invoiceBlob);
          } catch (e) {
            console.warn("Skipping invoice image due to fetch error:", url, e);
          }
          imageIdx += 1;
        }
        index += 1;
      }

      const timestamp = new Date().toISOString().replace(/[:T]/g, "-").split(".")[0];
      const content = await zip.generateAsync({ type: "blob" });
      saveAs(content, `Expenses-Images-${timestamp}.zip`);
    } catch (e) {
      console.error("Export images zip failed", e);
    } finally {
      setExportingImages(false);
      if (hiddenRenderRef.current) hiddenRenderRef.current.innerHTML = "";
    }
  };

  const handleRowSelectionChange = (newModel) => {
    setSelectedRowIds(newModel);
  };

  return (
    <>
      <div className="row px-[5px] pb-10">
        <div className="col-12">
          <div className="row d-flex align-items-end">
            <div className="col-12">
              <ListHeader
                mainHeading="Expense"
                placeholder="Search"
                btnShow={true}
                searchShow={true}
                showLikeFilter={false}
                DropDown={false}
                handleSearchChange={handleSearchChange}
                showWorkerFilter={true}
                handleProjectSearchChange={handleExpenseSearchChange}
                searchValue={searchQuery}
                moduleType="expenses"
                initialProjectId={location.state?.projectId}
                showProjectFilter={true}
              />
            </div>
          </div>
        </div>
        <div className="w-full px-2 pt-2 flex justify-end items-center gap-2 flex-wrap">
          <button
            className="ml-2 flex items-center px-3 py-2 rounded-[4px] border-none bg-[#F97316] text-white hover:bg-orange-600"
            onClick={() => setShowCreateDrawer(true)}
          >
            Add Expense
          </button>
          <button
            className="ml-2 flex items-center px-3 py-2 rounded-md border border-[#1E3A8A] bg-[#1E3A8A] text-white hover:opacity-90"
            onClick={handleExportExcel}
          >
            <FileSpreadsheet size={16} className="mr-2" />
            Export Excel
          </button>
          <button
            className="ml-2 flex items-center px-3 py-2 rounded-md border border-[#1E3A8A] bg-[#1E3A8A] text-white hover:opacity-90"
            onClick={handleExportPDF}
          >
            <FileText size={16} className="mr-2" />
            Export PDF
          </button>
        </div>
        <div className="w-full px-2 pt-3">
          <DG
            index={5}
            loader={loader}
            data={ExpenseData}
            getData={() => handlegetData(itemsPerPage, currentPage)}
            handleEdit={(row) => {
              // Prefer full original object if available
              setEditingExpense(row?.original || row);
              setShowEditDrawer(true);
            }}
            handleDelete={(row) => {
              setPendingDeleteId(row?.id);
              setConfirmDeleteOpen(true);
            }}
            enableCheckboxSelection
            disableRowSelectionOnClick
            rowSelectionModel={selectedRowIds}
            onRowSelectionModelChange={handleRowSelectionChange}
          />
        </div>
        <CustomPagination
          totalItems={totalExpense}
          itemsPerPage={itemsPerPage}
          currentPage={currentPage}
          onPageChange={handlePageChange}
        />

        {/* Hidden offscreen render area for image export */}
        <div
          ref={hiddenRenderRef}
          style={{ position: "fixed", top: -10000, left: -10000 }}
          aria-hidden
        />

        <CreateExpenseSideBar
          visible={showCreateDrawer}
          onClose={() => setShowCreateDrawer(false)}
          onSuccess={() => handlegetData(itemsPerPage, currentPage)}
          defaultProjectId={projectId}
          defaultVendorId={vendorId}
        />

        <EditExpenseSideBar
          visible={showEditDrawer}
          onClose={() => setShowEditDrawer(false)}
          onSuccess={() => handlegetData(itemsPerPage, currentPage)}
          expense={editingExpense}
        />

        <ConfirmationModal
          show={confirmDeleteOpen}
          handleClose={() => setConfirmDeleteOpen(false)}
          handleConfirm={async () => {
            if (!pendingDeleteId) return;
            try {
              await deleteExpense(pendingDeleteId);
              toast.success("Expense deleted successfully");
              setConfirmDeleteOpen(false);
              setPendingDeleteId(null);
              handlegetData(itemsPerPage, currentPage);
            } catch (e) {
              toast.error(e?.response?.data?.message || e?.message || "Failed to delete expense");
            }
          }}
          loading={false}
          message="Do you really want to delete this expense?"
        />

        {/* Bottom-center confirmation popup with slide/opacity animation */}
        {(() => {
          const shouldShow = (selectedColumns.length > 0 || (selectedRowIds?.length || 0) > 0);
          return (
            <div
              className="fixed left-1/2 z-30"
              style={{
                bottom: 24,
                transform: shouldShow ? "translate(-50%, 0)" : "translate(-50%, 16px)",
                opacity: shouldShow ? 1 : 0,
                pointerEvents: shouldShow ? "auto" : "none",
                transition: "transform 200ms ease, opacity 200ms ease",
              }}
            >
              <div className="flex items-center gap-3 bg-white text-black border border-gray-200 shadow-lg rounded-md px-4 py-3">
                <div className="text-sm">
                  {exportingImages ? (
                    "Preparing images..."
                  ) : (
                    (() => {
                      const totalRows = Array.isArray(ExpenseData) ? ExpenseData.length : 0;
                      const selectedCount = (selectedRowIds?.length || 0) || totalRows;
                      return `Export ${selectedCount} row(s)`;
                    })()
                  )}
                </div>
                <button
                  disabled={
                    exportingImages ||
                    !Array.isArray(ExpenseData) ||
                    ExpenseData.length === 0
                  }
                  onClick={exportSelectedColumnsAsImages}
                  className={`px-3 py-1.5 rounded-md text-white ${exportingImages ? "bg-gray-400" : "bg-[#1E3A8A] hover:opacity-90"}`}
                >
                  {exportingImages ? "Exporting..." : "Export Images (ZIP)"}
                </button>
                <button
                  disabled={exportingImages}
                  onClick={() => {
                    handleClearSelection();
                    setSelectedRowIds([]);
                  }}
                  className="px-2 py-1 text-sm text-gray-700 hover:text-gray-900"
                >
                  Clear
                </button>
              </div>
            </div>
          );
        })()}
      </div>
    </>
  );
};

export default Expenses;
